import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Bookmark, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { H5, Small } from '../../components/Typography';
import FlexBox from '../../components/flexbox/FlexBox';

const JiwongumAICard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [bookmarkedItems, setBookmarkedItems] = useState(new Set());

  // Default funding data for favorites/bookmarks
  const defaultFundingData = [
    {
      id: 1,
      title: '2025년 여성창업 상세세이지 교육 및 컨설팅',
      category: 'AI 한줄 요약',
      amount: '9,500만원',
      deadline: '25.01.06',
      tags: ['서울 전체', '소상공인', '고용안정'],
      description:
        '신산업 책임보험 최대 700만원/년 내 지원이 제공됩니다.\n해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
      logo: '/static/logo/SMEs.png',
    },
  ];

  const dataToUse = defaultFundingData;

  const toggleBookmark = itemId => {
    setBookmarkedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleCardClick = item => {
    navigate('/naver/callmetellme/jiwongum/post-detail');
  };
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f0f0f0',
      }}
    >
      <Grid container spacing={isMobile ? 3 : 3}>
        <Grid
          onClick={() => handleCardClick(dataToUse[0])}
          item
          xs={12}
          key={dataToUse[0].id}
        >
          <Box
            sx={{
              cursor: 'pointer',
              backgroundColor: '#fcfcfc',
              borderRadius: '24px',
              padding: isTablet ? '16px' : '20px',
              transition: 'all 0.2s ease-in-out',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': isMobile
                ? {}
                : {
                    transform: 'translateY(-2px)',
                  },
            }}
          >
            {/* Card Header */}
            <Box flex={1}>
              <Box
                display="flex"
                alignItems="center"
                gap={isMobile ? 1 : 1.5}
                mb={isMobile ? 1 : 1.5}
                flexWrap="wrap"
              >
                {dataToUse[0].tags &&
                  dataToUse[0].tags.map((tag, tagIndex) => (
                    <Box
                      key={tagIndex}
                      sx={{
                        backgroundColor: theme.palette.primary.mainBlueLight,
                        color: theme.palette.primary.mainBlue,
                        padding: isMobile ? '2px 8px' : '4px 12px',
                        borderRadius: '100px',
                        fontSize: isMobile ? '12px' : '13px',
                        fontWeight: '500',
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              flexDirection="row"
              mb={1}
            >
              <Box
                sx={{
                  width: isMobile ? '44px' : '50px',
                  height: isMobile ? '44px' : '50px',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: isMobile ? '12px' : '16px',
                  flexShrink: 0,
                  border: `1px solid ${theme.palette.primary.borderColor}`,
                  borderRadius: '100px',
                }}
              >
                <img
                  src={dataToUse[0].logo || '/static/logo/SMEs.png'}
                  alt="logo"
                  style={{
                    width: isMobile ? '48px' : '52px',
                    borderRadius: '100px',
                  }}
                />
              </Box>
              <H5
                sx={{
                  fontWeight: 'bold',
                  lineHeight: 1.3,
                  marginBottom: '8px',
                  fontSize: isMobile ? '16px' : '18px',
                }}
              >
                {dataToUse[0].title}
              </H5>
            </Box>

            <Box mb={1} sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  backgroundColor: '#f8f8f8',
                  padding: '16px',
                  borderRadius: '24px',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={isMobile ? 1 : 1.5}
                >
                  <Box
                    sx={{
                      width: isMobile ? '24px' : '28px',
                      height: isMobile ? '24px' : '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/static/icons/magic-wand.svg"
                      style={{
                        width: isMobile ? '26px' : '32px',
                        height: isMobile ? '26px' : '32px',
                      }}
                      alt="magic wand"
                    />
                  </Box>
                  <H5
                    sx={{
                      color: theme.palette.primary.mainBlue,
                      fontSize: isMobile ? '16px' : '18px',
                    }}
                  >
                    {dataToUse[0].category || 'AI 한줄 요약'}
                  </H5>
                </Box>

                {dataToUse[0].description &&
                  dataToUse[0].description.split('\n').map((line, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="flex-start"
                      mb={0.5}
                    >
                      <Box
                        sx={{
                          width: '4px',
                          height: '4px',
                          backgroundColor: theme.palette.text.secondary,
                          borderRadius: '50%',
                          marginTop: '6px',
                          marginRight: '8px',
                          flexShrink: 0,
                        }}
                      />
                      <Small
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.4,
                          fontSize: isMobile ? '14px' : '15px',
                        }}
                      >
                        {line}
                      </Small>
                    </Box>
                  ))}
              </Box>
            </Box>

            {/* Footer */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: isMobile ? 2 : 3,
                marginTop: 'auto',
              }}
            >
              <FlexBox
                sx={{
                  minWidth: isMobile ? 'auto' : '120px',
                  alignItems: 'center',
                }}
              >
                <Small
                  sx={{
                    color: theme.palette.text.secondary,
                    marginRight: '12px',
                  }}
                >
                  지원 금액
                </Small>
                <Small
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                  }}
                >
                  {dataToUse[0].amount}
                </Small>
              </FlexBox>
              <Box
                textAlign="right"
                sx={{ minWidth: isMobile ? 'auto' : '100px' }}
              >
                <Small
                  sx={{
                    color: theme.palette.text.secondary,
                    marginRight: '12px',
                  }}
                >
                  신청 기간
                </Small>
                <Small
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                  }}
                >
                  {dataToUse[0].deadline}
                </Small>
              </Box>
            </Box>

            <Box display="flex" gap={isMobile ? 1 : 1.5} mt={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: isMobile ? 'none' : 'scale(1.05)',
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  toggleBookmark(dataToUse[0].id);
                }}
              >
                <Bookmark
                  size={isMobile ? 19 : 21}
                  color={
                    bookmarkedItems.has(dataToUse[0].id)
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt
                  }
                  fill={
                    bookmarkedItems.has(dataToUse[0].id)
                      ? theme.palette.primary.mainBlue
                      : 'none'
                  }
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: isMobile ? 'none' : 'scale(1.05)',
                  },
                }}
                onClick={e => e.stopPropagation()}
              >
                <Share2
                  size={isMobile ? 19 : 21}
                  color={theme.palette.primary.greytxt}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default JiwongumAICard;
